import React, { useState, useEffect } from "react";
import { FaTimes, FaCalculator, FaRedo } from "react-icons/fa";
import "../styling/interestcalculator.css";

function calcInterest(principal, rate, rateType, fromDate, toDate) {
  const start = new Date(fromDate);
  const end   = new Date(toDate);
  if (end <= start) return null;

  const msPerDay  = 1000 * 60 * 60 * 24;
  const totalDays = Math.floor((end - start) / msPerDay);

  let years  = end.getFullYear() - start.getFullYear();
  let months = end.getMonth()    - start.getMonth();
  let days   = end.getDate()     - start.getDate();
  if (days < 0) {
    months--;
    days += new Date(end.getFullYear(), end.getMonth(), 0).getDate();
  }
  if (months < 0) { years--; months += 12; }

  const totalMonths = years * 12 + months + days / 30;
  const monthlyRate = rateType === "yearly" ? rate / 12 : rate;
  const interest    = (principal * monthlyRate * totalMonths) / 100;

  return {
    interest:  Math.round(interest            * 100) / 100,
    total:     Math.round((principal+interest) * 100) / 100,
    years, months, days, totalDays,
  };
}

const fmt = (n) =>
  Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InterestCalculator({ onClose }) {
  const today = new Date().toISOString().split("T")[0];

  const [principal, setPrincipal] = useState("");
  const [rate,      setRate]      = useState("");
  const [rateType,  setRateType]  = useState("monthly");
  const [fromDate,  setFromDate]  = useState("");
  const [toDate,    setToDate]    = useState(today);
  const [result,    setResult]    = useState(null);
  const [animated,  setAnimated]  = useState(false);
  const [error,     setError]     = useState("");

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const handleCalc = () => {
    setError("");
    const p = parseFloat(principal);
    const r = parseFloat(rate);
    if (!p || p <= 0)       { setError("Enter a valid principal amount."); return; }
    if (!r || r <= 0)       { setError("Enter a valid interest rate."); return; }
    if (!fromDate)          { setError("Select a start date."); return; }
    if (!toDate)            { setError("Select an end date."); return; }
    if (toDate <= fromDate) { setError("End date must be after start date."); return; }
    const res = calcInterest(p, r, rateType, fromDate, toDate);
    if (!res)               { setError("Invalid date range."); return; }
    setResult({ ...res, principal: p, rate: r, rateType });
    setAnimated(false);
    setTimeout(() => setAnimated(true), 40);
  };

  const handleReset = () => {
    setPrincipal(""); setRate(""); setRateType("monthly");
    setFromDate(""); setToDate(today);
    setResult(null); setError("");
  };

  const durationParts = result
    ? [
        result.years  > 0 ? `${result.years}Y`  : "",
        result.months > 0 ? `${result.months}M` : "",
        result.days   >= 0 ? `${result.days}D`  : "",
      ].filter(Boolean).join("  ")
    : "";

  const durationFull = result
    ? [
        result.years  > 0 ? `${result.years} Year${result.years   > 1 ? "s" : ""}` : "",
        result.months > 0 ? `${result.months} Month${result.months > 1 ? "s" : ""}` : "",
        result.days   > 0 ? `${result.days} Day${result.days     > 1 ? "s" : ""}` : "",
      ].filter(Boolean).join(", ") || "0 Days"
    : "";

  return (
    <div className="ic-overlay" onClick={onClose}>
      <div className="ic-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ─────────────────────────────────── */}
        <div className="ic-header">
          <div className="ic-header-left">
            <div className="ic-icon-wrap"><FaCalculator /></div>
            <div>
              <h2 className="ic-title">Interest Calculator</h2>
              <p className="ic-subtitle">Calculate interest between any two dates</p>
            </div>
          </div>
          <div className="ic-header-actions">
            <button className="ic-action-btn" onClick={handleReset} title="Reset all fields">
              <FaRedo />
            </button>
            <button className="ic-action-btn ic-action-btn--close" onClick={onClose} title="Close">
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="ic-gold-bar" />

        {/* ── Two-panel body ──────────────────────────── */}
        <div className="ic-panels">

          {/* LEFT — Inputs */}
          <div className="ic-left">
            <div className="ic-section-label">Loan Details</div>

            <div className="ic-field">
              <label className="ic-label">Principal Amount (₹)</label>
              <input className="ic-input" type="number" placeholder="e.g. 200000"
                value={principal} onChange={(e) => setPrincipal(e.target.value)} min="0" />
            </div>

            <div className="ic-field">
              <label className="ic-label">Interest Rate (%)</label>
              <input className="ic-input" type="number" placeholder="e.g. 3"
                value={rate} onChange={(e) => setRate(e.target.value)} min="0" step="0.01" />
            </div>

            <div className="ic-field">
              <label className="ic-label">Rate Type</label>
              <div className="ic-toggle-group">
                <button
                  className={`ic-toggle-btn ${rateType === "monthly" ? "ic-toggle-btn--on" : ""}`}
                  onClick={() => setRateType("monthly")}
                >
                  📅 Monthly
                </button>
                <button
                  className={`ic-toggle-btn ${rateType === "yearly" ? "ic-toggle-btn--on" : ""}`}
                  onClick={() => setRateType("yearly")}
                >
                  📆 Yearly
                </button>
              </div>
            </div>

            <div className="ic-divider" />
            <div className="ic-section-label">Date Range</div>

            <div className="ic-field">
              <label className="ic-label">From Date</label>
              <input className="ic-input" type="date"
                value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>

            <div className="ic-field">
              <label className="ic-label">
                To Date
                <span className="ic-today-badge">today</span>
              </label>
              <input className="ic-input" type="date"
                value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>

            {error && <p className="ic-error">⚠ {error}</p>}

            <button className="ic-calc-btn" onClick={handleCalc}>
              Calculate →
            </button>
          </div>

          {/* RIGHT — Results */}
          <div className="ic-right">
            {!result ? (
              <div className="ic-empty-result">
                <div className="ic-empty-glyph">✦</div>
                <p className="ic-empty-text">Results will appear here</p>
                <p className="ic-empty-sub">Fill in the details and click Calculate</p>
              </div>
            ) : (
              <div className={`ic-result-panel ${animated ? "ic-result-panel--in" : ""}`}>

                {/* Duration */}
                <div className="ic-dur-card">
                  <div className="ic-dur-label">Duration</div>
                  <div className="ic-dur-big">{durationParts}</div>
                  <div className="ic-dur-full">{durationFull}</div>
                  <div className="ic-dur-days">{result.totalDays} days total</div>
                </div>

                {/* Principal */}
                <div className="ic-res-card">
                  <div className="ic-res-label">Principal Amount</div>
                  <div className="ic-res-val ic-res-val--plain">₹{fmt(result.principal)}</div>
                </div>

                {/* Interest */}
                <div className="ic-res-card ic-res-card--gold">
                  <div className="ic-res-label">Interest Earned</div>
                  <div className="ic-res-val ic-res-val--gold">₹{fmt(result.interest)}</div>
                  <div className="ic-res-sub">
                    @ {rateType === "monthly" ? rate : (rate / 12).toFixed(3)}% per month
                  </div>
                </div>

                {/* Total */}
                <div className="ic-total-card">
                  <div className="ic-total-label">Total Amount Due</div>
                  <div className="ic-total-val">₹{fmt(result.total)}</div>
                  <div className="ic-total-breakdown">
                    <span>₹{fmt(result.principal)}</span>
                    <span className="ic-plus">+</span>
                    <span className="ic-interest-part">₹{fmt(result.interest)}</span>
                  </div>
                </div>

              </div>
            )}
          </div>

        </div>{/* end ic-panels */}
      </div>
    </div>
  );
}
